import {
	AddLink,
	CampaignOutlined,
	CheckCircleOutline,
	ContentCopy,
	HowToReg,
	Send,
	Visibility,
} from '@mui/icons-material';
import { Box, Chip, Paper, Typography, useTheme } from '@mui/material';
import { ReactNode } from 'react';
import { IndicacaoLink } from '../../../services/indicacao';
import { formatDate, withAlpha } from '../indicacao.utils';
import { EmptyState } from './EmptyState';
import { IndicacaoButton } from './IndicacaoButton';
import { SectionHeader } from './SectionHeader';

const ChipLink = ({ ativo }: { ativo: boolean }) => (
	<Chip
		size="small"
		label={ativo ? 'Pronto para enviar' : 'Inativo'}
		sx={{
			fontWeight: 700,
			color: ativo ? 'rgb(5, 150, 105)' : 'rgb(100, 116, 139)',
			bgcolor: ativo ? 'rgba(5, 150, 105, 0.12)' : '#f1f5f9',
		}}
	/>
);

const CampoResumo = ({
	icon,
	label,
	value,
	destaque,
}: {
	icon: ReactNode;
	label: string;
	value: string;
	destaque?: boolean;
}) => (
	<Box
		sx={{
			p: 1.5,
			borderRadius: 2.5,
			minWidth: 0,
			border: '1px solid',
			borderColor: destaque ? 'rgba(5, 150, 105, 0.28)' : '#e2e8f0',
			bgcolor: destaque ? 'rgba(5, 150, 105, 0.06)' : '#f8fafc',
		}}
	>
		<Box display="flex" alignItems="center" gap={0.75} mb={0.5}>
			<Box sx={{ display: 'flex', color: destaque ? 'rgb(5, 150, 105)' : 'rgb(100, 116, 139)' }}>
				{icon}
			</Box>
			<Typography
				fontSize="0.7rem"
				fontWeight={700}
				letterSpacing="0.06em"
				textTransform="uppercase"
				color="text.secondary"
			>
				{label}
			</Typography>
		</Box>
		<Typography fontWeight={700} fontSize="0.95rem" noWrap title={value}>
			{value}
		</Typography>
	</Box>
);

const PassosIndicacao = ({ temLink }: { temLink: boolean }) => {
	const theme = useTheme();
	const primary = theme.palette.primary.main;
	const passos = [
		{
			icon: temLink ? <ContentCopy sx={{ fontSize: 18 }} /> : <AddLink sx={{ fontSize: 18 }} />,
			texto: temLink ? 'Copie o link' : 'Gere o seu link',
		},
		{ icon: <Send sx={{ fontSize: 18 }} />, texto: 'Envie para o lojista' },
		{ icon: <HowToReg sx={{ fontSize: 18 }} />, texto: 'O cadastro fica com você' },
	];

	return (
		<Box display="grid" gridTemplateColumns={{ xs: '1fr', sm: 'repeat(3, 1fr)' }} gap={1}>
			{passos.map((passo, index) => (
				<Box
					key={passo.texto}
					display="flex"
					alignItems="center"
					gap={1}
					sx={{
						p: 1.25,
						borderRadius: 2,
						bgcolor: withAlpha(primary, 0.04),
						border: `1px solid ${withAlpha(primary, 0.2)}`,
					}}
				>
					<Box
						sx={{
							width: 28,
							height: 28,
							borderRadius: '50%',
							flexShrink: 0,
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'center',
							bgcolor: primary,
							color: '#fff',
							fontWeight: 800,
							fontSize: '0.8rem',
						}}
					>
						{index + 1}
					</Box>
					<Box sx={{ display: 'flex', color: primary }}>{passo.icon}</Box>
					<Typography fontSize="0.875rem" fontWeight={700}>
						{passo.texto}
					</Typography>
				</Box>
			))}
		</Box>
	);
};

interface LinkHeroProps {
	isAdmin?: boolean;
	totalVisitantes?: number;
	totalCadastros?: number;
	linkPrincipal?: IndicacaoLink;
	outrosLinks: IndicacaoLink[];
	linkCopiadoId: number | null;
	generating: boolean;
	onGerarLink: () => void;
	onCopiar: (link: IndicacaoLink) => void;
}

export function LinkHero({
	isAdmin,
	totalVisitantes = 0,
	totalCadastros = 0,
	linkPrincipal,
	outrosLinks,
	linkCopiadoId,
	generating,
	onGerarLink,
	onCopiar,
}: LinkHeroProps) {
	const theme = useTheme();
	const primary = theme.palette.primary.main;

	return (
		<Paper
			elevation={0}
			sx={{
				p: { xs: 2, sm: 3 },
				borderRadius: 3,
				border: `1px solid ${withAlpha(primary, 0.28)}`,
				background: `linear-gradient(180deg, ${withAlpha(primary, 0.12)} 0%, #fff 46%)`,
				height: '100%',
			}}
		>
			<Box mb={2}>
				<SectionHeader
					icon={<CampaignOutlined />}
					titulo={isAdmin ? 'Desempenho dos links' : 'Indique um lojista'}
					descricao={
						isAdmin
							? 'Quantidade de pessoas que abriram os links e de cadastros feitos no período.'
							: 'Envie o seu link. Quem abrir e se cadastrar fica vinculado a você.'
					}
					acao={
						isAdmin || linkPrincipal ? undefined : (
							<IndicacaoButton
								label={generating ? 'Gerando...' : 'Gerar meu link'}
								icon={<AddLink />}
								loading={generating}
								onClick={onGerarLink}
							/>
						)
					}
				/>
			</Box>
			{isAdmin ? (
				<Box display="grid" gridTemplateColumns={{ xs: '1fr', sm: '1fr 1fr' }} gap={1.5}>
					<CampoResumo
						icon={<Visibility sx={{ fontSize: 18 }} />}
						label="Pessoas que abriram"
						value={String(totalVisitantes)}
					/>
					<CampoResumo
						icon={<HowToReg sx={{ fontSize: 18 }} />}
						label="Cadastros feitos"
						value={String(totalCadastros)}
						destaque
					/>
				</Box>
			) : (
				<Box mb={2}>
					<PassosIndicacao temLink={Boolean(linkPrincipal)} />
				</Box>
			)}

			{isAdmin ? null : !linkPrincipal ? (
				<EmptyState
					destaque
					icon={<AddLink />}
					texto="Você ainda não tem um link. Gere o seu para começar a indicar lojistas."
					acao={
						<IndicacaoButton
							label={generating ? 'Gerando...' : 'Gerar meu link'}
							icon={<AddLink />}
							loading={generating}
							onClick={onGerarLink}
						/>
					}
				/>
			) : (
				<Box display="flex" flexDirection="column" gap={2}>
					<Box
						sx={{
							p: 2,
							borderRadius: 2.5,
							border: `1px solid ${withAlpha(primary, 0.22)}`,
							bgcolor: '#fff',
						}}
					>
						<Box
							display="flex"
							justifyContent="space-between"
							alignItems="center"
							gap={1}
							flexWrap="wrap"
							mb={1.5}
						>
							<Box>
								<Typography fontWeight={800}>Este é o link para indicar</Typography>
								<Typography fontSize="0.8rem" color="text.secondary">
									Criado em {formatDate(linkPrincipal.data_cadastro)}
								</Typography>
							</Box>
							<ChipLink ativo={linkPrincipal.ativo} />
						</Box>
						<Box
							display="flex"
							flexDirection={{ xs: 'column', sm: 'row' }}
							alignItems={{ xs: 'stretch', sm: 'center' }}
							gap={1}
							sx={{
								p: 1.25,
								borderRadius: 2,
								bgcolor: withAlpha(primary, 0.06),
								border: `1px solid ${withAlpha(primary, 0.22)}`,
							}}
						>
							<Typography
								sx={{
									flex: 1,
									wordBreak: 'break-all',
									fontSize: '0.95rem',
									fontWeight: 700,
									lineHeight: 1.5,
								}}
							>
								{linkPrincipal.url_gerada}
							</Typography>
							<IndicacaoButton
								label={linkCopiadoId === linkPrincipal.id ? 'Copiado' : 'Copiar link'}
								icon={
									linkCopiadoId === linkPrincipal.id ? (
										<CheckCircleOutline />
									) : (
										<ContentCopy />
									)
								}
								onClick={() => onCopiar(linkPrincipal)}
							/>
						</Box>
						<Box display="grid" gridTemplateColumns={{ xs: '1fr', sm: '1fr 1fr' }} gap={1.5} mt={1.5}>
							<CampoResumo
								icon={<Visibility sx={{ fontSize: 18 }} />}
								label="Pessoas que abriram"
								value={String(linkPrincipal.total_visitantes)}
							/>
							<CampoResumo
								icon={<HowToReg sx={{ fontSize: 18 }} />}
								label="Cadastros feitos"
								value={String(linkPrincipal.total_cadastrados_por_link)}
								destaque
							/>
						</Box>
					</Box>
					{outrosLinks.length > 0 && (
						<Box>
							<Typography fontWeight={800} mb={1}>
								Outros links
							</Typography>
							<Box display="flex" flexDirection="column" gap={1}>
								{outrosLinks.map((link) => (
									<Box
										key={link.id}
										sx={{
											p: 1.5,
											borderRadius: 2,
											border: '1px solid #e2e8f0',
											display: 'flex',
											flexDirection: { xs: 'column', sm: 'row' },
											alignItems: { xs: 'stretch', sm: 'center' },
											justifyContent: 'space-between',
											gap: 1.5,
										}}
									>
										<Box minWidth={0} display="flex" flexDirection="column" gap={0.75}>
											<Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
												<Typography fontSize="0.875rem" fontWeight={700}>
													Criado em {formatDate(link.data_cadastro)}
												</Typography>
												<ChipLink ativo={link.ativo} />
											</Box>
											<Typography fontSize="0.8rem" color="text.secondary">
												{link.total_visitantes}{' '}
												{link.total_visitantes === 1 ? 'pessoa abriu' : 'pessoas abriram'}
												{' · '}
												{link.total_cadastrados_por_link}{' '}
												{link.total_cadastrados_por_link === 1
													? 'cadastro feito'
													: 'cadastros feitos'}
											</Typography>
										</Box>
										<IndicacaoButton
											size="small"
											variant="outlined"
											label={linkCopiadoId === link.id ? 'Copiado' : 'Copiar'}
											icon={
												linkCopiadoId === link.id ? (
													<CheckCircleOutline fontSize="small" />
												) : (
													<ContentCopy fontSize="small" />
												)
											}
											onClick={() => onCopiar(link)}
										/>
									</Box>
								))}
							</Box>
						</Box>
					)}
				</Box>
			)}
		</Paper>
	);
}
